import React, { useEffect, useRef, useState } from 'react';
import { BrandMark } from './BrandMark';
import { ExampleSchedule } from './ExampleSchedule';
import { SignUpFlow } from './SignUpFlow';
import { FoundingFooter } from './FoundingFooter';
import '../LandingPageV2.css';
import './FoundingMemberPage.css';

const BENEFITS = [
  { n: '01', title: 'Know the cost first.', text: 'See what your project should cost before any builder visits.' },
  { n: '02', title: 'Compare quotes properly.', text: 'Every builder prices the same list, so you compare like for like.' },
  { n: '03', title: 'Fewer nasty surprises.', text: 'Everything is agreed up front, so extras do not appear halfway through.' },
];

const PROS = [
  { role: 'Builder', label: 'For builders', heading: 'Price jobs without measuring every room', text: 'Quote from a clear, measured list. Less time estimating, fewer wrong assumptions and fewer disputes over what was included.', cta: 'Join as a builder' },
  { role: 'Architect', label: 'For architects', heading: 'Give clients a cost check at design stage', text: "Test the design against the client's budget early, and issue a measured scope with your tender drawings. No full QS appointment needed.", cta: 'Join as an architect' },
];

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

export default function FoundingMemberPage() {
  const inputRef = useRef(null);
  const heroRef = useRef(null);
  const [pendingRole, setPendingRole] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'SchedulesOfWork | Founding members';
    setMeta('description', 'Know what your renovation should cost before the builders arrive. Join free as a founding member.');
    return () => { document.title = prevTitle; };
  }, []);

  const joinAs = (role) => {
    setPendingRole(role);
    heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 450);
  };

  return (
    <div className="landing-v2 fm-page" data-testid="founding-page">
      <header className="lv2-nav"><div className="lv2-nav-inner"><BrandMark /></div></header>

      <section className="lv2-hero fm-hero" data-testid="founding-hero">
        <div className="lv2-hero-glow" />
        <div className="lv2-hero-grid" />
        <div className="fm-hero-inner">
          <div className="fm-hero-copy" ref={heroRef}>
            <h1 className="lv2-hero-h1 fm-hero-h1" data-testid="founding-headline">Start your refurbishment on the same page as your builder.</h1>
            <p className="lv2-hero-sub fm-hero-sub">Send us your floor plan. We turn it into a clear schedule of work for every job, so you know the cost and every builder quotes for the same work.</p>
            <SignUpFlow ref={inputRef} pendingRole={pendingRole} />
          </div>
          <div className="fm-hero-visual"><ExampleSchedule /></div>
        </div>
      </section>

      <section className="lv2-value-props" data-testid="benefits-section">
        <div className="lv2-vp-grid">
          {BENEFITS.map((b) => (
            <div key={b.n} className="lv2-vp-card fm-benefit">
              <div className="fm-benefit-n">{b.n}</div>
              <div className="lv2-vp-title">{b.title}</div>
              <div className="lv2-vp-desc">{b.text}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="fm-mission" data-testid="mission-section">
        <div className="fm-mission-inner">
          <p className="fm-mission-text">Professional cost advice used to be too expensive for smaller projects. We built SchedulesOfWork so homeowners go to builders with confidence, and builders price clear information instead of guessing.</p>
        </div>
      </section>

      <section className="fm-pros" data-testid="pros-section">
        <div className="fm-owner-cta">
          <button type="button" className="lv2-p-btn fm-owner-btn" onClick={() => joinAs(null)} data-testid="join-as-homeowner-investor-btn">Join as a homeowner or property investor</button>
        </div>
        <div className="fm-pros-grid">
          {PROS.map((p) => (
            <div key={p.role} className="fm-pro-card" data-testid={`pro-card-${p.role.toLowerCase()}`}>
              <div className="lv2-section-eyebrow">{p.label}</div>
              <h3 className="fm-pro-h">{p.heading}</h3>
              <p className="fm-pro-p">{p.text}</p>
              <button type="button" className="lv2-p-btn fm-pro-btn" onClick={() => joinAs(p.role)} data-testid={`join-as-${p.role.toLowerCase()}-btn`}>{p.cta}</button>
            </div>
          ))}
        </div>
      </section>

      <FoundingFooter />
    </div>
  );
}
